import React, { useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import { Box, IconButton } from '@mui/material';

import { Typography } from '../Typography/Typography';
import { Popover } from '../Popover/Popover';
import { Button } from '../Button/Button';
import { Chip } from '../Chip/Chip';
import { i18n } from '../../types/i18n';
import { isI18n, MenuOptionSelection } from '../../utils';
import { Icon } from '../Icon/Icon';
import { theme } from '../../theme';
import { pluralize } from '../../utils';

export interface TagFilterV2Item {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface TagFilterV2Option {
  category: string;
  items: TagFilterV2Item[];
}

export interface TagFilterV2Props {
  anchorTitle?: string | i18n;
  containerTitle?: string | i18n;
  confirmButtonLabel?: string | i18n;
  clearButtonLabel?: string | i18n;
  options: TagFilterV2Option[];
  showCount?: boolean;
  showInlineSelections?: boolean;
  appliedValue: string[];
  onApply?: (value: string[]) => void;
  onDraftChange?: (value: string[]) => void;
  onCancel?: () => void;
  v2?: boolean;
  results: MenuOptionSelection[];
}

export const TagFilterV2: React.FC<TagFilterV2Props> = ({
  anchorTitle,
  containerTitle,
  confirmButtonLabel: confirmButtonLabelProp,
  clearButtonLabel: clearButtonLabelProp,
  options,
  appliedValue,
  showCount = true,
  showInlineSelections = true,
  onDraftChange,
  onApply,
  onCancel,
  v2,
  results,
}) => {
  const [draftSelections, setDraftSelections] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isOpen = Boolean(anchorEl);

  const popoverId = isOpen ? 'tag-filter-popover' : undefined;

  const containerTitleLabel = containerTitle
    ? isI18n(containerTitle)
      ? containerTitle.fallback
      : containerTitle
    : 'Filters';

  const containerTitleI18n = isI18n(containerTitle)
    ? containerTitle
    : undefined;

  const anchorTitleLabel = (() => {
    const label = anchorTitle
      ? isI18n(anchorTitle)
        ? anchorTitle.fallback
        : anchorTitle
      : 'Filters';

    if (showCount && appliedValue.length > 0) {
      return `${label} (${appliedValue.length})`;
    }

    return label;
  })();

  const anchorTitleI18n = isI18n(anchorTitle) ? anchorTitle : undefined;

  const confirmButtonLabel = confirmButtonLabelProp
    ? isI18n(confirmButtonLabelProp)
      ? confirmButtonLabelProp.fallback
      : confirmButtonLabelProp
    : 'Apply';

  const confirmButtonI18n = isI18n(confirmButtonLabelProp)
    ? confirmButtonLabelProp
    : undefined;

  const clearButtonLabel = clearButtonLabelProp
    ? isI18n(clearButtonLabelProp)
      ? clearButtonLabelProp.fallback
      : clearButtonLabelProp
    : 'Clear all';

  const clearButtonI18n = isI18n(clearButtonLabelProp)
    ? clearButtonLabelProp
    : undefined;

  const appliedItems = useMemo(() => {
    if (!showInlineSelections) {
      return [];
    }

    return uniqBy(options.map((opt) => opt.items).flat(), 'value').filter(
      (item) => appliedValue.includes(item.value)
    );
  }, [appliedValue, options, showInlineSelections]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftSelections(appliedValue);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    handleClose();
    onCancel?.();
  };

  const handleClose = () => {
    setDraftSelections([]);
    setAnchorEl(null);
  };

  const handleToggle = (item: TagFilterV2Item, isSelected: boolean) => {
    if (!isOpen) {
      // Changes should be applied to the actual value
      onApply?.(
        isSelected
          ? appliedValue.filter((v) => v !== item.value)
          : [...appliedValue, item.value]
      );
      return;
    }

    // If is open changes should be applied to draft
    const newDraftSelections = isSelected
      ? draftSelections.filter((v) => v !== item.value)
      : [...draftSelections, item.value];
    setDraftSelections(newDraftSelections);

    onDraftChange?.(newDraftSelections);
  };

  const handleConfirm = () => {
    onApply?.(draftSelections);
    handleClose();
  };

  const handleClearAll = () => {
    setDraftSelections([]);
    onApply?.([]);
  };

  /************ V2 updates *********************/

  const confirmButtonColor = v2 ? 'primary' : 'secondary';

  /*********************************************/

  const renderItem = (
    item: TagFilterV2Item,
    action: 'click' | 'delete',
    isSelected: boolean
  ) => {
    const selectedStyle = isSelected
      ? {}
      : { border: `1px solid ${theme.palette.secondary.dark}` };
    const variant = isSelected ? 'filled' : 'outlined';
    const color = v2 ? 'success' : 'primary';

    const toggleFn = () => handleToggle(item, isSelected);

    return (
      <Chip
        key={item.value}
        color={color}
        variant={variant}
        label={item.label}
        disabled={item.disabled}
        onClick={action === 'click' ? toggleFn : undefined}
        onDelete={action === 'delete' ? toggleFn : undefined}
        size="small"
        sx={{
          ...selectedStyle,
          height: '24px',
          fontSize: '13px',
        }}
        v2={v2}
      />
    );
  };

  const renderAppliedItems = () => {
    return (
      <Box display="flex" flexWrap="wrap" columnGap={1} rowGap={1}>
        {appliedItems.map((item) => renderItem(item, 'delete', true))}
      </Box>
    );
  };

  const renderOption = (option: TagFilterV2Option) => {
    const { category, items } = option;

    return (
      <Box mb={1} key={option.category}>
        <Typography variant="body1" mb={1}>
          {category}
        </Typography>
        <Box display="flex" flexWrap="wrap" columnGap={1} rowGap={1}>
          {items.map((item) => {
            const isSelected = draftSelections.includes(item.value);
            return renderItem(item, 'click', isSelected);
          })}
        </Box>
      </Box>
    );
  };

  const renderFooter = () => {
    return (
      <Box display="flex" justifyContent="flex-end" columnGap={2}>
        <Button
          onClick={handleClearAll}
          variant="text"
          i18n={clearButtonI18n}
          disabled={!draftSelections?.length}
        >
          {clearButtonLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmButtonColor}
          i18n={confirmButtonI18n}
        >
          {confirmButtonLabel}
        </Button>
      </Box>
    );
  };

  const renderContent = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        rowGap={2}
        minWidth="320px"
        maxWidth="480px"
      >
        <Box display="flex" justifyContent="space-between">
          <Typography
            variant="h6"
            i18nKey={containerTitleI18n?.key}
            i18nComponents={containerTitleI18n?.components}
            i18nParams={containerTitleI18n?.params}
          >
            {containerTitleLabel}
          </Typography>
          <IconButton color="inherit" onClick={handleClose}>
            <Icon name="close-outline" fill="currentColor" size="large" />
          </IconButton>
        </Box>
        {options.map((option) => renderOption(option))}
        {renderFooter()}
      </Box>
    );
  };

  const filteredResultsLabel = appliedItems.length
    ? `Showing ${pluralize(results.length, 'result')} based in`
    : null;

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        columnGap={1}
        rowGap={1}
        alignItems="center"
      >
        <Button
          startEvaIcon={{ name: 'funnel-outline' }}
          onClick={handlePopoverOpen}
          variant="outlined"
          sx={{ flexShrink: 0 }}
          i18n={anchorTitleI18n}
        >
          {anchorTitleLabel}
        </Button>
      </Box>
      <Popover
        id={popoverId}
        open={isOpen}
        onClose={handlePopoverClose}
        anchorEl={anchorEl}
        PaperProps={{ variant: 'elevation' }}
        v2={v2}
      >
        {renderContent()}
      </Popover>
      {!!appliedItems.length && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color={theme.palette.text.secondary}
            i18nKey="tagFilter:container.showingResults"
            i18nParams={{ count: results.length }}
          >
            {filteredResultsLabel}
          </Typography>
          <Button
            color="primary"
            size="large"
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
            onClick={handleClearAll}
            i18nKey="tagFilter:container.clearFilters"
          >
            CLEAR ALL
          </Button>
        </Box>
      )}
      {!!appliedItems.length && renderAppliedItems()}
    </>
  );
};
